/**
 * @fileoverview User Profile Context - Focused on basic user data and authentication
 * Part of Phase 2.3 context optimization - split from monolithic UserContext
 */

import { createContext, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useStore } from '../../store';
import { useAddError } from '../ErrorContext';
import { ApiError, ErrorCode } from '../../services/api/errors';
import type { UserProfile, UserTier } from '../../types/user';

export interface UserProfileContextType {
    // Core user data
    profile: UserProfile | null;
    tier: UserTier;

    // Loading and error states
    isLoading: boolean;
    error: string | null;

    // Actions
    refreshUserData: () => Promise<void>;
}

export const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

interface UserProfileProviderProps {
    children: ReactNode;
}

export const UserProfileProvider = ({ children }: UserProfileProviderProps) => {
    const store = useStore();
    const addError = useAddError();

    // Extract only profile-related data from store
    const { profile, isLoading, error, tier } = store;

    // Action to refresh user data with integrated error handling
    const refreshUserData = useCallback(async () => {
        try {
            await store.fetchUserProfile();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            const apiError = new ApiError(
                ErrorCode.INTERNAL_SERVER_ERROR,
                'Failed to refresh user profile',
                { originalError: errorMessage }
            );
            addError(apiError);

            // Log for debugging
        }
    }, [store, addError]);

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo((): UserProfileContextType => ({
        profile,
        tier,
        isLoading,
        error,
        refreshUserData
    }), [profile, tier, isLoading, error, refreshUserData]);

    return (
        <UserProfileContext.Provider value={contextValue}>
            {children}
        </UserProfileContext.Provider>
    );
};
